import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import type { ClickCounterRow, MapMode } from "../lib/types";

/**
 * Live click counters for a given map mode, kept in sync in real time via
 * Supabase Realtime (Postgres change feed on free_click_counters /
 * premium_click_counters) - this is what replaces SignalR from the original
 * spec: any open tab sees another visitor's click within roughly a second,
 * with no polling.
 */
export function useCountryClicks(mode: MapMode) {
  const table = mode === "free" ? "free_click_counters" : "premium_click_counters";
  const [rows, setRows] = useState<Map<number, ClickCounterRow>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    async function load() {
      const { data, error } = await supabase.from(table).select("*");
      if (!active) return;
      if (error) {
        console.error(`failed to load ${table}`, error);
      } else if (data) {
        setRows(new Map((data as ClickCounterRow[]).map((row) => [row.country_numeric, row])));
      }
      setLoading(false);
    }
    load();

    const channel = supabase
      .channel(`${table}-realtime`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        (payload) => {
          setRows((prev) => {
            const next = new Map(prev);
            if (payload.eventType === "DELETE") {
              const oldRow = payload.old as Partial<ClickCounterRow>;
              if (oldRow.country_numeric !== undefined) next.delete(oldRow.country_numeric);
            } else {
              const row = payload.new as ClickCounterRow;
              next.set(row.country_numeric, row);
            }
            return next;
          });
        },
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, [table]);

  const maxClicks = useMemo(() => {
    let max = 0;
    for (const row of rows.values()) {
      if (row.clicks > max) max = row.clicks;
    }
    return max;
  }, [rows]);

  return { rows, maxClicks, loading };
}
